import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Flame, TrendingUp, TrendingDown, Minus, Trophy, Skull } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface PlayerStreak {
  id: string;
  player_id: string;
  current_streak: number;
  streak_type: string;
  best_win_streak: number;
  best_loss_streak: number;
  player?: { id: string; name: string; avatar: string | null };
}

export const PlayerStreaks = () => {
  const [streaks, setStreaks] = useState<PlayerStreak[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStreaks();
  }, []);

  const fetchStreaks = async () => {
    const { data } = await supabase
      .from('player_streaks')
      .select(`
        *,
        player:players!player_streaks_player_id_fkey(id, name, avatar)
      `)
      .gt('current_streak', 0)
      .order('current_streak', { ascending: false })
      .limit(10);

    if (data) setStreaks(data as any);
    setLoading(false);
  };

  const getStreakIcon = (type: string) => {
    switch (type) {
      case 'win': return TrendingUp;
      case 'loss': return TrendingDown;
      default: return Minus;
    }
  };

  const getStreakColor = (type: string) => {
    switch (type) {
      case 'win': return 'text-green-500 bg-green-500/20';
      case 'loss': return 'text-red-500 bg-red-500/20';
      default: return 'text-yellow-500 bg-yellow-500/20';
    }
  };

  const getStreakLabel = (type: string) => {
    switch (type) {
      case 'win': return 'জয়ের স্ট্রিক';
      case 'loss': return 'হারের স্ট্রিক';
      default: return 'ড্র স্ট্রিক';
    }
  };

  if (loading) {
    return (
      <Card className="p-4 animate-pulse">
        <div className="h-32 bg-muted rounded" />
      </Card>
    );
  }

  // Separate win and loss streaks
  const winStreaks = streaks.filter(s => s.streak_type === 'win').slice(0, 5);
  const lossStreaks = streaks.filter(s => s.streak_type === 'loss').slice(0, 3);

  return (
    <Card className="p-4 bg-gradient-to-br from-card/80 to-card border-primary/20">
      <div className="flex items-center gap-2 mb-4">
        <Flame className="w-5 h-5 text-orange-500" />
        <h3 className="font-bold text-lg">Player Streaks</h3>
      </div>

      {streaks.length === 0 ? (
        <div className="text-center text-muted-foreground py-8">
          <Flame className="w-12 h-12 mx-auto mb-2 opacity-30" />
          <p>কোন স্ট্রিক নেই</p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Hot Streaks (Winning) */}
          {winStreaks.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Trophy className="w-4 h-4 text-green-500" />
                <span className="text-sm font-medium text-green-500">🔥 Hot Streaks</span>
              </div>
              <div className="space-y-2">
                {winStreaks.map((streak, idx) => {
                  const IconComponent = getStreakIcon(streak.streak_type);
                  const colorClass = getStreakColor(streak.streak_type);

                  return (
                    <div
                      key={streak.id}
                      className="flex items-center gap-3 p-2 rounded-lg bg-green-500/5 border border-green-500/20"
                    >
                      <span className="text-sm font-bold text-green-500 w-5">{idx + 1}</span>
                      <Avatar className="w-8 h-8 border border-green-500/30">
                        <AvatarImage src={streak.player?.avatar || ''} />
                        <AvatarFallback>{streak.player?.name?.[0] || '?'}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{streak.player?.name}</p>
                      </div>
                      <Badge className={`${colorClass} border-0`}>
                        <IconComponent className="w-3 h-3 mr-1" />
                        {streak.current_streak} জয়
                      </Badge>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Cold Streaks (Losing) */}
          {lossStreaks.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Skull className="w-4 h-4 text-red-500" />
                <span className="text-sm font-medium text-red-500">❄️ Cold Streaks</span>
              </div>
              <div className="space-y-2">
                {lossStreaks.map((streak) => {
                  const IconComponent = getStreakIcon(streak.streak_type);
                  const colorClass = getStreakColor(streak.streak_type);

                  return (
                    <div
                      key={streak.id}
                      className="flex items-center gap-3 p-2 rounded-lg bg-red-500/5 border border-red-500/20"
                    >
                      <Avatar className="w-8 h-8 border border-red-500/30">
                        <AvatarImage src={streak.player?.avatar || ''} />
                        <AvatarFallback>{streak.player?.name?.[0] || '?'}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{streak.player?.name}</p>
                      </div>
                      <Badge className={`${colorClass} border-0`}>
                        <IconComponent className="w-3 h-3 mr-1" />
                        {streak.current_streak} হার
                      </Badge>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Best Records */}
          <div className="pt-2 border-t border-border/50">
            <p className="text-xs text-muted-foreground text-center">
              সেরা জয়ের স্ট্রিক: {Math.max(...streaks.map(s => s.best_win_streak), 0)} ম্যাচ
            </p>
          </div>
        </div>
      )}
    </Card>
  );
};
